import { useQuery, gql, type QueryHookOptions } from "@apollo/client";

const PROPERTY_LISTING_QUERY = gql`
  query PropertyListing($slug: String!) {
    propertyListing(slug: $slug) {
      id
      publicRemarks
      listingId
      bedroomsTotal
      bathroomTotal
      parking
      parkingType
      moreInformationLink
      lease
      leasePerUnit
      leasePerTime
      price
      addressLine1
      city
      province
      postalCode
      unitNumber
      propertyType
      buildingType
      storiesTotal
      communityName
      ownershipType
      sizeTotalText
      constructedDate
      ammenitiesNearBy
      communityFeatures
      parkingSpaceTotal
      roadType
      basementDevelopment
      basementType
      halfBathTotal
      coolingType
      exteriorFinish
      flooringType
      heatingFuel
      heatingType
      constructionStyleAttachment
      sewer
      utilityWater
      images {
        id
        url
      }
      listingAgent {
        id
        name
        website
        position
        telephone
        teamMember {
          id
          slug
          name
          avatarThumb
        }
        office {
          id
          name
          addressLine1
          city
          province
          postalCode
          website
          telephone
        }
      }
    }
  }
`;

interface PropertyListingVariables {
  slug: string;
}

interface Office {
  id: string;
  name: string;
  addressLine1: string | undefined;
  city: string | undefined;
  province: string | undefined;
  postalCode: string | undefined;
  website: string | undefined;
  telephone: string | undefined;
}

interface TeamMember {
  id: string;
  slug: string;
  name: string;
  avatarThumb: string | undefined;
}

interface ListingAgent {
  id: string;
  name: string;
  website: string | undefined;
  position: string | undefined;
  telephone: string | undefined;
  teamMember: TeamMember;
  office: Office | null;
}

interface ListingImage {
  id: string;
  url: string | undefined;
}

interface PropertyListing {
  id: string;
  publicRemarks: string | null;
  listingId: string | null;
  bedroomsTotal: string | null;
  bathroomTotal: string | null;
  parking: string;
  parkingType: string | null;
  moreInformationLink: string | null;
  lease: string | null;
  leasePerUnit: string | null;
  leasePerTime: string | null;
  price: string | null;
  addressLine1: string | null;
  city: string | null;
  province: string | null;
  postalCode: string | null;
  unitNumber: string | null;
  propertyType: string | null;
  buildingType: string | null;
  storiesTotal: string | null;
  communityName: string | null;
  ownershipType: string | null;
  sizeTotalText: string | null;
  constructedDate: string | null;
  ammenitiesNearBy: string | null;
  communityFeatures: string | null;
  parkingSpaceTotal: string | null;
  roadType: string | null;
  basementDevelopment: string | null;
  basementType: string | null;
  halfBathTotal: string | null;
  coolingType: string | null;
  exteriorFinish: string | null;
  flooringType: string | null;
  heatingFuel: string | null;
  heatingType: string | null;
  constructionStyleAttachment: string | null;
  sewer: string | null;
  utilityWater: string | null;
  images: ListingImage[];
  listingAgent: ListingAgent | null;
}

interface PropertyListingData {
  propertyListing: PropertyListing;
}

const usePropertyListingQuery = (
  options?: QueryHookOptions<PropertyListingData, PropertyListingVariables>,
) => {
  return useQuery<PropertyListingData, PropertyListingVariables>(PROPERTY_LISTING_QUERY, options);
};

export { usePropertyListingQuery };
export type {
  PropertyListingData,
  ListingAgent,
  Office,
  PropertyListing,
  ListingImage,
  TeamMember,
};
